import {ActionMenu, ActionList, Button, LinkButton} from '@primer/react'
import {CopilotIcon, PlusIcon, TriangleDownIcon} from '@primer/octicons-react'
import {FileCheckIcon} from '@github-ui/pacer/CustomIcon'
import {useSubAgents} from '../../hooks/useSubAgents'
import {useTopLevelAgents} from '../../hooks/useTopLevelAgents'
import {AgentListItems} from './AgentListItems'

import styles from './AgentSelector.module.css'
import type {DropdownRepository} from '../../hooks/useCopilotRepoFetching'
import {useMemo} from 'react'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {IconButtonSelector} from '@github-ui/copilot-chat/components/IconButtonSelector'
import {isFeatureEnabled} from '@github-ui/feature-flags'

type AgentSelectorProps = {
  repo: DropdownRepository
  branch: string
  selectedAgent: string | null
  onSelect: (agent: string | null) => void
  iconOnly?: boolean
  disabled?: boolean
}

export function AgentSelector({
  repo,
  branch,
  selectedAgent,
  onSelect,
  iconOnly = true,
  disabled = false,
}: AgentSelectorProps) {
  const {data: customAgents = [], isLoading: isLoadingCustom} = useSubAgents({repo})
  const use3pAgentInfo = isFeatureEnabled('copilot_3p_agent_info')
  const {data: topLevelAgents = [], isLoading: isLoadingTopLevel} = useTopLevelAgents(
    repo.nameWithOwner,
    use3pAgentInfo,
  )

  // Default to "Copilot" if feature flag is enabled and no agent is selected
  const effectiveSelectedAgent = use3pAgentInfo && !selectedAgent ? 'Copilot' : selectedAgent

  const isLoading = isLoadingCustom || isLoadingTopLevel
  const agents = [...topLevelAgents, ...customAgents]
  const selectedAgentObj = agents.find(agent => agent.name === effectiveSelectedAgent)
  const selectedAgentName = selectedAgentObj?.display_name || selectedAgentObj?.name

  const newFileUrl = useMemo(() => {
    const params = new URLSearchParams({
      filename: '.github/agents/my-agent.agent.md',
      value:
        '---\n# Fill in the fields below to create a basic custom agent for your repository.\n# The Copilot CLI can be used for local testing: https://gh.io/customagents/cli\n# To make this agent available, merge this file into the default repository branch.\n# For format details, see: https://gh.io/customagents/config\n\nname:\ndescription:\n---\n\n# My Agent\n\nDescribe what your agent does here...',
    })
    return `/${repo.owner}/${repo.nameOnly}/new/${branch}?${params.toString()}`
  }, [repo, branch])

  const triggerLabel = selectedAgentName || 'None'
  const ariaLabel = selectedAgentName
    ? `${use3pAgentInfo ? 'Agent' : 'Custom agent'}: ${selectedAgentName}`
    : `Select ${use3pAgentInfo ? 'an agent' : 'a custom agent'}`

  return (
    <ActionMenu>
      <ActionMenu.Anchor>
        {iconOnly ? (
          <IconButtonSelector
            icon={CopilotIcon}
            selectedIcon={FileCheckIcon}
            aria-label={ariaLabel}
            selected={Boolean(selectedAgentObj)}
            disabled={disabled || isLoading}
          />
        ) : (
          <Button
            leadingVisual={selectedAgentName ? FileCheckIcon : CopilotIcon}
            trailingAction={TriangleDownIcon}
            aria-label={ariaLabel}
            className={styles.anchor}
            data-selected={Boolean(selectedAgentObj)}
            disabled={disabled || isLoading}
          >
            {triggerLabel}
          </Button>
        )}
      </ActionMenu.Anchor>
      <ActionMenu.Overlay width="auto">
        {agents.length === 0 ? (
          <EmptyState newFileUrl={newFileUrl} />
        ) : (
          <ActionList>
            {!use3pAgentInfo && (
              <ActionList.Group selectionVariant="single">
                <ActionList.Item selected={!selectedAgent} onSelect={() => onSelect(null)}>
                  None
                </ActionList.Item>
              </ActionList.Group>
            )}
            <AgentListItems
              topLevelAgents={topLevelAgents}
              customAgents={customAgents}
              selectedAgent={effectiveSelectedAgent}
              onSelectAgent={() => onSelect(null)} // TODO: onSelect should handle selecting top-level agents
              onSelectCustomAgent={agentName => onSelect(agentName)}
              showTopLevelAgents={use3pAgentInfo}
            />
            <ActionList.Divider />
            <ActionList.Group selectionVariant={false}>
              <ActionList.LinkItem href={newFileUrl} target="_blank">
                <ActionList.LeadingVisual>
                  <PlusIcon />
                </ActionList.LeadingVisual>
                Create a custom agent
              </ActionList.LinkItem>
            </ActionList.Group>
          </ActionList>
        )}
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

const EmptyState = ({newFileUrl}: {newFileUrl: string}) => (
  <div className={styles.emptyStateContainer}>
    <h3 className={styles.emptyStateTitle}>This repository has no custom agents</h3>
    <p className={styles.emptyStateDescription}>
      Custom agents are reusable instructions and tools in your repository.
    </p>
    <LinkButton href={newFileUrl}>Create a custom agent</LinkButton>
  </div>
)

try{ AgentSelector.displayName ||= 'AgentSelector' } catch {}
try{ EmptyState.displayName ||= 'EmptyState' } catch {}